import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { personLookup } from 'reducers/PersonReducer';
import PersonIcon from '@material-ui/icons/PersonOutlined';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import { useDispatch } from 'react-redux';
import { RMSAutocomplete } from '../../../shared/components/form-controls/index';
import { executeApiAction } from '../../utility/apiHelper';
import { getPersonById } from '../../../shared/services/generalServices';
import { RMSButton, RMSIconButton } from '../../../shared/components/buttons/index';
import { useSelector } from 'react-redux';
import EditPersonDialog from '../../../user/components/common/EditPersonDialog';

const SearchPersonAutoComplete = ({
  name,
  label,
  onSelectionChange,
  meButtonVisible = false,
  placeholder,
  autoFocus = false,
  defaultPersonId = null,
  defaultPersonSelectedCallback = null,
  disabled = false,
  showAddIcon = true,
  onAddIconClick = () => {},
  showDetailsIcon = true,
  searchPersonCallback = null,
  disableClearable = false,
  optionValue = 'ptsPersonID',
}) => {
  const dispatch = useDispatch();
  const isMounted = useRef(true);
  const currentUser = useSelector((state) => state.user.userData.user);
  // Matches legacy's PersonLookup2.js exactly — the inline Add-person icon is hidden
  // (not disabled) unless the user has People.Create.
  const personPerms = getFullPermissions('globals', 'People', 'any');
  const canCreatePerson = personPerms.Create;
  // Beyond legacy (which always shows this icon unconditionally) — the Person Details icon
  // is now hidden entirely unless the user has at least Read or Edit on People.
  const canViewPersonDetails = Boolean(personPerms.Read || personPerms.Edit);

  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editPersonId, setEditPersonId] = useState(null);

  const searchPersons = useCallback(
    async (searchText) => {
      const searchFunction = searchPersonCallback || personLookup;
      await executeApiAction({
        setLoading,
        action: async () => {
          return searchFunction(searchText);
        },
        onSuccess: (response) => setPersons(response),
        onError: (error) => {
          dispatch(notify(error.message, 'error'));
        },
      });
    },
    [dispatch, searchPersonCallback]
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((searchText) => {
        if (!searchText || searchText.trim().length < 3) {
          setPersons([]);
          return;
        }

        searchPersons(searchText);
      }, 500),
    [searchPersons]
  );

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleDefaultPersonId = useCallback(
    async (personId, callback = defaultPersonSelectedCallback) => {
      if (!personId) return;

      await executeApiAction({
        setLoading,
        dispatch,
        action: () => getPersonById(personId),
        onSuccess: (response) => {
          setPersons((prev) => {
            const exists = prev.some((person) => person.ptsPersonID === response.ptsPersonID);
            return exists ? prev : [response, ...prev];
          });

          callback?.(response);
        },
      });
    },
    [defaultPersonSelectedCallback, dispatch]
  );

  const handleMeClick = useCallback(() => {
    if (!currentUser?.ptsPersonID) {
      dispatch(notify('Logged in user not found.', 'warning'));
      return;
    }

    handleDefaultPersonId(currentUser.ptsPersonID, onSelectionChange);
  }, [currentUser, dispatch, handleDefaultPersonId, onSelectionChange]);

  useEffect(() => {
    handleDefaultPersonId(defaultPersonId);
  }, [defaultPersonId, handleDefaultPersonId]);

  const handlePersonUpdated = useCallback(
    (person) => {
      if (!person) return;

      setPersons((prev) =>
        prev.map((existing) =>
          existing.ptsPersonID === person.ptsPersonID ? { ...existing, ...person } : existing
        )
      );

      onSelectionChange?.(person);
    },
    [onSelectionChange]
  );

  return (
    <Box className="rms-person-autocomplete-wrapper">
      {meButtonVisible && (
        <RMSButton
          label="Me"
          size="small"
          className="rms-me-chip"
          disabled={disabled}
          onClick={handleMeClick}
        />
      )}

      <RMSAutocomplete
        name={name}
        label={label}
        placeholder={placeholder}
        getOptionLabel={(option) => `${option.FirstName} ${option.LastName}`}
        optionLabel="FirstName"
        optionValue={optionValue}
        options={persons}
        leftContent={() =>
          showAddIcon &&
          canCreatePerson && (
            <RMSIconButton
              icon={<AddIcon fontSize="small" />}
              tooltip="Add Person"
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onAddIconClick?.(event);
              }}
            />
          )
        }
        rightContent={({ value }) =>
          showDetailsIcon &&
          value &&
          canViewPersonDetails && (
            <RMSIconButton
              icon={<PersonIcon />}
              tooltip="Person Details"
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                setEditPersonId(value.ptsPersonID);
              }}
            />
          )
        }
        onSelectionChange={(data) => onSelectionChange && onSelectionChange(data)}
        onSearch={debouncedSearch}
        disabled={disabled}
        loading={loading}
        autoFocus={autoFocus}
        disableClearable={disableClearable}
      />

      {editPersonId && (
        <EditPersonDialog
          open={Boolean(editPersonId)}
          personId={editPersonId}
          onClose={() => setEditPersonId(null)}
          onPersonUpdated={handlePersonUpdated}
        />
      )}
    </Box>
  );
};

export default SearchPersonAutoComplete;
